import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { setAnalyticsUiLanguage, trackUiLanguageChanged } from '../analytics/posthog'
import { I18nContext, type Dict } from './context'
import { UI_LOCALES, UI_LANG_STORAGE_KEY, detectLocale, pathLocale } from './config'

/** Provides the UI language. English is baked into the bundle (the landing
 * stays fast); every other dictionary is a lazy chunk fetched on first use
 * and kept for the session. */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => detectLocale())
  const [dicts, setDicts] = useState<Record<string, Dict>>({})

  const meta = UI_LOCALES.find((l) => l.code === locale)
  const dict = locale === 'en' ? null : (dicts[locale] ?? null)

  // Reflect the language on the document — screen readers, fonts, RTL layout
  useEffect(() => {
    document.documentElement.lang = meta?.bcp47 ?? 'en'
    document.documentElement.dir = meta?.rtl ? 'rtl' : 'ltr'
  }, [meta])

  // Every analytics event carries the interface language as a super property
  useEffect(() => {
    setAnalyticsUiLanguage(locale)
  }, [locale])

  useEffect(() => {
    if (locale === 'en' || dicts[locale]) return
    let cancelled = false
    import(`./locales/${locale}.json`)
      .then((m) => {
        if (cancelled) return
        const d = (m.default ?? m) as Dict
        setDicts((prev) => ({ ...prev, [locale]: d }))
      })
      .catch(() => {
        // Missing/broken dictionary — the UI just stays English
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  // Arrived with an explicit URL signal (?lang= or a /{code}/ locale home) —
  // persist it as the new choice; strip only the ?lang param (the path stays,
  // it's a real page). Slash-less /ru is normalized to the canonical /ru/ —
  // prod nginx 301s it before the app loads, this covers environments
  // without nginx (vite preview, dev).
  useEffect(() => {
    const url = new URL(window.location.href)
    const hasParam = url.searchParams.has('lang')
    const onLocaleHome = pathLocale() !== null
    if (!hasParam && !onLocaleHome) return
    try {
      localStorage.setItem(UI_LANG_STORAGE_KEY, locale)
    } catch {
      // storage unavailable — detection will re-run next visit
    }
    if (hasParam) url.searchParams.delete('lang')
    const pathname =
      onLocaleHome && !url.pathname.endsWith('/') ? `${url.pathname}/` : url.pathname
    if (hasParam || pathname !== url.pathname) {
      window.history.replaceState(null, '', pathname + url.search + url.hash)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(
    () => ({
      locale,
      bcp47: meta?.bcp47 ?? 'en',
      dict,
      setLocale: (code: string) => {
        if (code !== locale) trackUiLanguageChanged(locale, code)
        setLocaleState(code)
        try {
          localStorage.setItem(UI_LANG_STORAGE_KEY, code)
        } catch {
          // storage unavailable — the choice lives for this session only
        }
        // Standing on a locale home (or the root)? Keep the URL in sync so a
        // reload lands on the language the user just picked
        if (window.location.pathname === '/' || pathLocale() !== null) {
          const newPath = code === 'en' ? '/' : `/${code}/`
          window.history.replaceState(null, '', newPath + window.location.search + window.location.hash)
        }
      },
    }),
    [locale, dict, meta],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
